import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Button, CircularProgress, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import type { JSX } from "react";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  useDeleteServiceApiValuationPackageValuationByItemIdMutation,
  useLazyGetServiceApiValuationPackageValuationByItemIdQuery,
  useLazyGetServiceApiValuationPackageValuationByItemIdReportPdfQuery,
  useLazyGetServiceApiValuationPackageValuationByValuationPackageItemIdImagesQuery,
  usePostServiceApiValuationPackageValuationByItemIdCopyMutation,
} from "@/api/enhancedGeneratedApi";
import { ValuationHubCoreModelImageRead } from "@/api/generatedApi";
import { DeleteIcon, Download01Icon, Edit02Icon, EyeOutlinedIcon } from "@/assets";
import { LoadingOverlay } from "@/components";
import { mapApiResponseToPropertyInfo } from "@/helpers/propertyDataMappers";
import { propertyImagesCompletionStatus } from "@/helpers/realEstateDataValidationAndCompletenessHelpers";
import {
  setRealEstateData,
  updateAvm,
  updateDemandScore,
  updateProsaWeb,
  updateReport,
  updateSone,
} from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";

const styles = {
  menuItemIcon: {
    color: "primary.main",
    fontSize: "16px",
  },
};

interface ValuationActionsProps {
  id: string;
  referenceNumber?: string | null;
  onDeleted?: (id: string) => void;
  isExpired?: boolean;
  hideDelete?: boolean;
}

export const ValuationActions = ({
  id,
  referenceNumber,
  onDeleted,
  isExpired,
  hideDelete = false,
}: ValuationActionsProps): JSX.Element => {
  const { t } = useTranslation("ValuationListPage");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [getValuation, { isFetching: isValuationFetching }] =
    useLazyGetServiceApiValuationPackageValuationByItemIdQuery();
  const [getImages, { isFetching: isImagesFetching }] =
    useLazyGetServiceApiValuationPackageValuationByValuationPackageItemIdImagesQuery();
  const [triggerDownloadReport, { isLoading: isReportLoading, isFetching: isReportFetching }] =
    useLazyGetServiceApiValuationPackageValuationByItemIdReportPdfQuery();
  const [deleteValuation, { isLoading: isDeleting }] = useDeleteServiceApiValuationPackageValuationByItemIdMutation();
  const [copyValuation, { isLoading: isCopying }] = usePostServiceApiValuationPackageValuationByItemIdCopyMutation();

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (isDeleting) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setAnchorEl(null);
  };

  const handleView = async (): Promise<void> => {
    handleClose();
    try {
      const valuationData = await getValuation({ itemId: id }).unwrap();

      const valuationResult = valuationData.valuation_result;

      const imagesResult = valuationData.real_estate?.property_information
        ? await getImages({ valuationPackageItemId: id })
        : null;

      const images = imagesResult?.data
        ? imagesResult.data
            .map((img: ValuationHubCoreModelImageRead) => ({
              id: img.id ?? "",
              url: img.url ?? "",
              title: img.title ?? "",
              order: img.order ?? 0,
            }))
            .sort((a, b) => a.order - b.order)
        : [];

      dispatch(
        setRealEstateData({
          id: valuationData.id,
          address: valuationData.real_estate?.address ?? null,
          propertyInformation: valuationData.real_estate?.property_information
            ? {
                ...mapApiResponseToPropertyInfo(
                  valuationData.real_estate?.property_information,
                  valuationData.real_estate?.rental_information,
                  valuationData.real_estate?.financial_information,
                ),
                propertyImages: {
                  images,
                  completionStatus: propertyImagesCompletionStatus(images),
                },
              }
            : undefined,
        }),
      );
      if (valuationResult) {
        dispatch(updateSone(valuationResult.sone ?? null));
        dispatch(updateAvm(valuationResult.avm ?? null));
        dispatch(updateProsaWeb(valuationResult.prosa_web ?? null));
        dispatch(updateDemandScore(valuationResult.demand_score ?? null));
      } else {
        dispatch(updateSone(null));
        dispatch(updateAvm(null));
        dispatch(updateProsaWeb(null));
        dispatch(updateDemandScore(null));
      }

      if (valuationData.reference_number) {
        dispatch(
          updateReport({
            reportAskingPrice: valuationData.report_asking_price ?? null,
            reportFileName: valuationData.reference_number,
            reportTimestamp: valuationData.report_created_at ?? null,
          }),
        );
      } else {
        dispatch(
          updateReport({
            reportAskingPrice: null,
            reportFileName: null,
            reportTimestamp: null,
          }),
        );
      }
      navigate(`/valuations/${valuationData.id}`);
    } catch (error) {
      console.error("Failed to load valuation:", error);
    }
  };

  const handleDownloadReport = async (): Promise<void> => {
    if (isReportLoading || isReportFetching) return;
    try {
      const result = await triggerDownloadReport({ itemId: id });

      if ("data" in result && result.data instanceof Blob) {
        const url = URL.createObjectURL(result.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = referenceNumber || `valuation-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        throw new Error("Failed to download report");
      }
    } catch (error) {
      console.error("Failed to download report:", error);
    } finally {
      handleClose();
    }
  };

  const handleDelete = async (): Promise<void> => {
    handleClose();
    try {
      await deleteValuation({ itemId: id }).unwrap();
      onDeleted?.(id);
    } catch (error) {
      console.error("Failed to delete valuation:", error);
    }
  };

  const handleCopy = async (): Promise<void> => {
    handleClose();
    try {
      const result = await copyValuation({ itemId: id }).unwrap();
      if (result?.id) {
        navigate(`/valuations/${result.id}`);
      }
    } catch (error) {
      console.error("Failed to copy valuation:", error);
    }
  };

  return (
    <>
      <LoadingOverlay open={isValuationFetching || isImagesFetching || isCopying} />

      <Button
        variant="outlined"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleClick}
        disabled={isValuationFetching || isImagesFetching || isDeleting || isCopying}
      >
        {t("valutionActionButton.title")}
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleView} disabled={isDeleting || isCopying} data-cy="valuation-item-menu-open">
          <ListItemIcon>
            <EyeOutlinedIcon sx={styles.menuItemIcon} />
          </ListItemIcon>
          <Typography>{t("valutionActionButton.open")}</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleDownloadReport}
          disabled={isDeleting || isCopying || !referenceNumber}
          data-cy="valuation-item-download-report"
        >
          <ListItemIcon>
            {isReportLoading || isReportFetching ? (
              <CircularProgress size={16} sx={{ color: "primary.main" }} />
            ) : (
              <Download01Icon sx={styles.menuItemIcon} />
            )}
          </ListItemIcon>
          <Typography>{t("valutionActionButton.download")}</Typography>
        </MenuItem>
        <MenuItem onClick={handleCopy} disabled={!isExpired || isCopying} data-cy="valuation-item-menu-copy">
          <ListItemIcon>
            <Edit02Icon sx={styles.menuItemIcon} />
          </ListItemIcon>
          <Typography>{t("valutionActionButton.new")}</Typography>
        </MenuItem>
        {!hideDelete && (
          <MenuItem onClick={handleDelete} disabled={isDeleting || isCopying} data-cy="valuation-item-menu-delete">
            <ListItemIcon>
              <DeleteIcon sx={styles.menuItemIcon} />
            </ListItemIcon>
            <Typography>{t("valutionActionButton.delete")}</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
